// frontend/src/pages/Home.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ShieldCheck } from 'lucide-react';
import SEO from '../components/SEO';
import WeeklySafetyLawUpdates from '../components/WeeklySafetyLawUpdates';
import { searchService } from '../services/searchService';

const fallbackKeywords = ['폭염 휴식시간', '안전관리비 사용 가능 품목', '안전관리자 선임 기준', '추락방지시설'];

const Home = () => {
  const navigate = useNavigate();
  const [homeSearchQuery, setHomeSearchQuery] = React.useState('');
  const [recommendedKeywords, setRecommendedKeywords] = React.useState(fallbackKeywords);

  React.useEffect(() => {
    let active = true;
    searchService.getPopularSearches(4, 30).then((items) => {
      if (!active || !items.length) return;
      setRecommendedKeywords(items.map((item) => item.keyword));
    });
    return () => { active = false; };
  }, []);

  const submitSearch = (event) => {
    event.preventDefault();
    const query = homeSearchQuery.trim();
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : '/search');
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '안전사관학교',
    description: '복잡하고 흩어진 안전기준을 쉽게 찾고 이해해 실제 현장 조치로 이어지도록 돕는 안전관리 실무 플랫폼',
    url: 'https://safety-academy.kr',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://safety-academy.kr/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEO
        title="안전기준 통합검색 | 안전사관학교"
        description="법령·질의회시·안전자재 기준을 한 번에 검색하고 최근 30일 시행·개정된 안전기준을 확인하세요."
        url="/"
        structuredData={structuredData}
      />
      <main className="min-h-screen bg-slate-50">
        <section className="relative isolate overflow-hidden bg-slate-950 px-4 py-16 sm:px-6 sm:py-24 lg:py-28" aria-labelledby="home-hero-title">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_50%_0%,rgba(16,185,129,0.22),transparent_48%)]" aria-hidden="true" />
          <div className="absolute -left-24 top-20 -z-10 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" aria-hidden="true" />
          <div className="absolute -right-24 bottom-0 -z-10 h-72 w-72 rounded-full bg-emerald-400/10 blur-3xl" aria-hidden="true" />

          <div className="mx-auto max-w-5xl text-center">
            <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-300/25 bg-emerald-400/10 px-4 py-2 text-sm font-bold text-emerald-200">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              안전관리 실무 통합검색
            </p>
            <h1 id="home-hero-title" className="text-4xl font-black tracking-tight text-white sm:text-5xl lg:text-6xl">
              현장 안전관리를 위해 필요한 정보,
              <span className="mt-2 block text-emerald-300">한 번에 찾아보세요</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-slate-300 sm:text-lg">
              법령, 질의회시, 안전자재 기준까지 흩어진 정보를 하나의 검색창에서 확인할 수 있습니다.
            </p>

            <form onSubmit={submitSearch} className="mx-auto mt-9 max-w-4xl rounded-3xl border border-white/15 bg-white/10 p-2 shadow-2xl shadow-emerald-950/40 backdrop-blur sm:p-3">
              <div className="relative flex items-center rounded-2xl bg-white p-1.5 sm:p-2">
                <Search className="absolute left-5 h-6 w-6 text-slate-400 sm:left-6" aria-hidden="true" />
                <input
                  autoFocus
                  value={homeSearchQuery}
                  onChange={(event) => setHomeSearchQuery(event.target.value)}
                  placeholder="예: 체감온도 33도 휴식시간, 안전모 구입비"
                  className="min-w-0 flex-1 rounded-xl border-0 bg-transparent py-4 pl-12 pr-3 text-base text-slate-900 outline-none placeholder:text-slate-400 focus:ring-0 sm:py-5 sm:pl-14 sm:text-lg"
                  aria-label="법령·질의회시·안전자재 통합 검색"
                />
                <button type="submit" className="shrink-0 rounded-xl bg-emerald-600 px-5 py-4 text-sm font-black text-white shadow-lg transition hover:bg-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-300/50 sm:px-8 sm:py-5 sm:text-base">
                  검색
                </button>
              </div>
            </form>

            <div className="mt-5 flex flex-wrap items-center justify-center gap-2" aria-label="인기 검색어">
              <span className="mr-1 text-xs font-bold text-emerald-300 sm:text-sm">인기 검색어</span>
              {recommendedKeywords.map((keyword) => (
                <button
                  key={keyword}
                  type="button"
                  onClick={() => navigate(`/search?q=${encodeURIComponent(keyword)}`)}
                  className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-slate-200 transition hover:border-emerald-300/40 hover:bg-white/10 sm:text-sm"
                >
                  {keyword}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
          <WeeklySafetyLawUpdates />
        </div>
      </main>
    </>
  );
};

export default Home;
